import {inject, Injectable} from '@angular/core';
import {EMPTY, Observable, of} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {ApiUtil} from '@shared/util/apiUtil';
import {
  ActivityTagDTO,
  CreateActivityTagAliasRequest,
  CreateActivityTagRequest
} from '@domain/activity-tag/activity-tag.dto';
import {CreatePlaceRequest, PlaceDTO} from '@domain/place/place.dto';
import {catchError} from 'rxjs/operators';
import { NotificationService } from '@shared/notification/notification.service';
import {ActivityDTO} from '@domain/activity/activity.dto';

@Injectable({
  providedIn: 'root'
})
export class ActivityTagApi {

  private readonly notifyService = inject(NotificationService);

  constructor(private readonly http: HttpClient) {}

  public getRoots(): Observable<ActivityTagDTO[]> {
    return this.http.get<ActivityTagDTO[]>(ApiUtil.buildUrl(this.getPath()));
  }

  public getById(tagId: string): Observable<ActivityTagDTO> {
    return this.http.get<ActivityTagDTO>(ApiUtil.buildUrl(this.getPath(tagId)));
  }

  public search(term: string): Observable<ActivityTagDTO[]> {
    return this.http.get<ActivityTagDTO[]>(
      ApiUtil.buildUrl(this.getPath(), `/search/${term}`)
    );
  }

  public getActivities(tagId: string): Observable<ActivityDTO[]> {
    return this.http.get<ActivityDTO[]>(
      ApiUtil.buildUrl(this.getPath(tagId), '/activity')
    );
  }

  public create(createRequest: CreateActivityTagRequest): Observable<ActivityTagDTO> {
    return this.http.post<ActivityTagDTO>(ApiUtil.buildUrl(this.getPath()), createRequest).pipe(
      catchError(err => {
        this.notifyService.toast({
          message: err?.error?.message ?? 'Erreur inconnue'
        });
        return EMPTY
      })
    );
  }

  public update(tagId: string, updateRequest: Partial<CreateActivityTagRequest>): Observable<ActivityTagDTO> {
    return this.http.patch<ActivityTagDTO>(ApiUtil.buildUrl(this.getPath(tagId)), updateRequest).pipe(
      catchError(err => {
        this.notifyService.toast({
          message: err?.error?.message ?? 'Erreur inconnue'
        });
        return EMPTY
      })
    );
  }

  public delete(tagId: string): Observable<void> {
    return this.http.delete<void>(ApiUtil.buildUrl(this.getPath(tagId))).pipe(
      catchError(err => {
        this.notifyService.toast({
          message: err?.error?.message ?? 'Erreur inconnue'
        });
        return EMPTY
      })
    );
  }

  public addAlias(tagId: string, createRequest: CreateActivityTagAliasRequest): Observable<ActivityTagDTO> {
    return this.http.post<ActivityTagDTO>(ApiUtil.buildUrl(this.getPath(tagId), `/alias`), createRequest);
  }

  public deleteAlias(tagId: string, aliasId: string): Observable<ActivityTagDTO> {
    return this.http.delete<ActivityTagDTO>(ApiUtil.buildUrl(this.getPath(tagId), `/alias/${aliasId}`));
  }

  public linkSecondaryParent(tagId: string, newParentId: string): Observable<ActivityTagDTO> {
    return this.http.post<ActivityTagDTO>(ApiUtil.buildUrl(this.getPath(tagId), `/parent/${newParentId}`), null);
  }

  public unlinkSecondaryParent(tagId: string, newParentId: string): Observable<ActivityTagDTO> {
    return this.http.delete<ActivityTagDTO>(ApiUtil.buildUrl(this.getPath(tagId), `/parent/${newParentId}`));
  }

  public exportAll(): Observable<Blob> {
    return this.http.get(ApiUtil.buildUrl(this.getPath(), '/export'), {responseType: 'blob'});
  }

  public importAll(file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<void>(ApiUtil.buildUrl(this.getPath(), '/import'), formData);
  }

  private getPath(tagId?: string): string {
    const prefix = `activity-tag`;
    return tagId
      ? `${prefix}/${tagId}`
      : prefix;
  }
}
